import { TALENT_PAGE_KEY } from './constants';

export const es = {
  [TALENT_PAGE_KEY]: {
    PAGE_TITLE: 'Talent',
    PAGE_SUBTITLE: 'Analiza el desempeño de la organización por proceso.',
    BREADCRUMB_TALENT: 'Talent',
    FRESHNESS_PREFIX: 'Actualizado',

    TAB_GOALS: 'Metas',
    TAB_COMPETENCIES: 'Competencias',

    // ANLT-598 — added by task-05 (task-06 hasn't landed yet as of this task);
    // grep before adding — do not redefine.
    PROCESS_LOADING: 'Cargando proceso',

    // ANLT-598 (task-06) — the four-field metadata row (D-1). Copy must say
    // "colaboradores inscritos", never "que cargaron metas" (BE rule 6).
    PROCESS_ENROLLED: '{{count}} colaborador inscrito',
    PROCESS_ENROLLED_plural: '{{count}} colaboradores inscritos',
    PROCESS_STATUS_IN_PROGRESS: 'En curso',
    PROCESS_STATUS_CLOSED: 'Cerrado',

    EMPTY_TITLE: 'Sin procesos todavía',
    EMPTY_DESCRIPTION:
      'Cuando se cree un proceso para este módulo, aparecerá aquí.',

    UNAVAILABLE_TITLE: 'Tablero no disponible',
    UNAVAILABLE_DESCRIPTION:
      'Tu organización no tiene contratado este módulo de Talent.',

    // ANLT-598 — needed by TalentDashboardErrorBoundary (task-03's route error.tsx),
    // not listed among the plan's "States" keys (those ship with task-06's content
    // state machine) because this pair backs the Modern.js ROUTE boundary, not
    // TalentDashboardContent's in-page error panel. Task-06: grep before adding —
    // do not redefine ERROR_TITLE/ERROR_RETRY.
    ERROR_TITLE: 'Error al cargar el dashboard de Talent',
    ERROR_RETRY: 'Reintentar',

    // ANLT-598 (task-06) — TalentDashboardContent's in-page catalog-error panel
    // (distinct from the route boundary above — the rest of the page stays
    // usable here). Named CONTENT_ERROR_* instead of the task-06.md sketch's
    // plain ERROR_TITLE/ERROR_DESCRIPTION to avoid redefining ERROR_TITLE
    // (already owned by the route boundary, different copy). ERROR_RETRY is
    // reused as-is — "Reintentar" fits both surfaces verbatim, no new key
    // needed. Stays generic — never discloses WHY error_code fired (BE rule 7).
    //
    // REPAIR (ANLT-598/security) — CONTENT_ERROR_DESCRIPTION is now ALSO
    // reused verbatim by `TalentDashboardErrorBoundary` (the route boundary
    // above) for its modules-access QUERY error branch, replacing a raw
    // `error.message` render. Grep before adding a third description key.
    CONTENT_ERROR_TITLE: 'No pudimos cargar la información',
    CONTENT_ERROR_DESCRIPTION:
      'Ocurrió un error al cargar los datos. Intenta de nuevo.',

    // ANLT-599 — modo Seguimiento/Resultados
    MODE_TRACKING: 'Seguimiento',
    MODE_RESULTS: 'Resultados',
    MODE_HINT: 'Seguimiento: cómo va el proceso. Resultados: cómo les fue.',
    HOWTO_TITLE: 'Cómo leer este tablero',
    HOWTO_PROGRESS_VS_COMPLIANCE:
      'Seguimiento mide avance del proceso (cuántas metas se cargaron, aprobaron y reportaron). Resultados mide cumplimiento (qué tan bien les fue).',
    HOWTO_NO_RESULT_IS_NOT_ZERO:
      'Quien no participó no promedia como cero: aparece como «Sin resultado».',
    MODE_TRACKING_PENDING_TITLE: 'Seguimiento llega pronto',
    MODE_TRACKING_PENDING_DESCRIPTION:
      'Los bloques de Seguimiento del ciclo de metas todavía no están disponibles en este tablero.',
    MODE_RESULTS_PENDING_TITLE: 'Resultados llega pronto',
    MODE_RESULTS_PENDING_DESCRIPTION:
      'Los bloques de Resultados del ciclo de metas todavía no están disponibles en este tablero.',

    // ANLT-599 (task-05) — M-S1: avance del ciclo por etapa
    CYCLE_HEADING: 'Avance del ciclo',
    CYCLE_DESCRIPTION:
      'Cuántos colaboradores están en cada etapa del proceso de metas.',
    CYCLE_TRACK_MISSING_NOTE:
      'Este proceso todavía no tiene un ciclo de seguimiento definido, así que la autoevaluación y la evaluación del líder aún no son medibles.',

    CYCLE_ENROLLED_LABEL: 'Colaboradores inscritos',
    CYCLE_ENROLLED_TOOLTIP:
      'Cuenta colaboradores asignados y activos hoy. Puede ser mayor al reporte «Resumen carga de metas» (que solo cuenta a quienes ya cargaron metas) y decrece con el tiempo en un proceso cerrado.',
    CYCLE_STAGE_BREAKDOWN: '{{completed}} completadas · {{pending}} pendientes',
    CYCLE_A11Y_BAND: 'Avance del ciclo por etapa',

    STAGE_LOADED: 'Metas creadas',
    STAGE_APPROVED: 'Metas aprobadas',
    STAGE_SELF_REPORTED: 'Autoevaluación',
    STAGE_BOSS_REPORTED: 'Evaluación del líder',

    CYCLE_EMPTY_TITLE: 'Todavía no hay avance que mostrar',
    CYCLE_EMPTY_DESCRIPTION:
      'Este proceso no tiene inscritos o seguimientos cargados todavía.',
    CYCLE_ERROR_TITLE: 'No pudimos cargar el avance del ciclo',

    // ANLT-600 (task-02) — M-S2: evolución del avance
    EVOLUTION_HEADING: 'Evolución del avance',
    EVOLUTION_DESCRIPTION:
      'Cuántos colaboradores completaron cada etapa, día a día del ciclo.',
    EVOLUTION_EMPTY_TITLE: 'Todavía no hay días que dibujar',
    EVOLUTION_EMPTY_DESCRIPTION:
      'Este proceso aún no tiene una ventana de fechas con seguimientos registrados.',
    EVOLUTION_ERROR_TITLE: 'No pudimos cargar la evolución del avance',

    // ANLT-600 (task-03) — línea de metadatos: ventana del proceso + fecha de
    // corte. Ambas fechas pasan SIEMPRE por `formatUtcDateLabel` (nunca el
    // reloj del navegador ni `toLocaleDateString` sin `timeZone: 'UTC'`).
    EVOLUTION_WINDOW: 'Ciclo {{start}} → {{end}}',
    EVOLUTION_DATA_AS_OF: 'Datos al {{today}}',
    EVOLUTION_TODAY_OUTSIDE:
      'El proceso cerró el {{end}}. Los datos están al {{today}}.',

    // ANLT-600 (task-04) — nota de honestidad (AC-4). Precedencia fija, plan
    // §8: `undefined` y `0` NUNCA imprimen línea (R3). `EVOLUTION_FOOTNOTE_NO_WINDOW`
    // es copy placeholder pendiente de confirmar con producto (R-2 del plan).
    EVOLUTION_FOOTNOTE_TITLE: 'Lo que la curva no muestra',
    EVOLUTION_FOOTNOTE_UNDRAWN_STAGES:
      'El origen de datos no guarda fecha para {{stages}}, así que esas etapas no se pueden dibujar. No son cero: son desconocidas.',
    EVOLUTION_FOOTNOTE_TRUNCATED:
      'La curva se cortó el {{lastDay}} por el límite de puntos de la serie; el proceso sigue hasta el {{end}}.',
    EVOLUTION_FOOTNOTE_WITHOUT_DATE:
      '{{self}} colaboradores en autoevaluación y {{boss}} en evaluación del líder completaron la etapa sin fecha registrada, así que no aparecen en la curva.',
    EVOLUTION_FOOTNOTE_AFTER_CURVE:
      '{{self}} en autoevaluación y {{boss}} en evaluación del líder completaron la etapa después del último día de la serie.',
    EVOLUTION_FOOTNOTE_RECONCILE:
      'Por eso la curva puede terminar por debajo de las tarjetas de «Avance del ciclo»: sumando lo de arriba, los dos números coinciden.',
    EVOLUTION_FOOTNOTE_NO_WINDOW:
      'No hay una ventana de fechas para este proceso todavía.',
    EVOLUTION_STAGE_LOADED: 'Metas creadas',
    EVOLUTION_STAGE_APPROVED: 'Metas aprobadas',

    // ANLT-600 (task-05) — andamio del gráfico: label del skeleton propio del
    // gráfico (distinto del Skeleton a nivel de sección, task-02) + títulos de
    // eje. El eje Y son colaboradores, nunca porcentaje — esta consulta no
    // trae denominador (riesgo R-1 del plan).
    EVOLUTION_LOADING_LABEL: 'Cargando la evolución del avance',
    EVOLUTION_AXIS_X: 'Día del ciclo',
    EVOLUTION_AXIS_Y: 'Colaboradores',

    // ANLT-600 (task-06) — dos series + marca de «hoy» (AC-2 visual, AC-3).
    // `EVOLUTION_TODAY_MARK` es el `label.value` de `CrehanaReferenceLine`,
    // renderizada solo cuando `today` es una de las categorías del eje.
    EVOLUTION_SERIES_SELF: 'Autoevaluación',
    EVOLUTION_SERIES_BOSS: 'Evaluación del líder',
    EVOLUTION_TODAY_MARK: 'Hoy',

    // ANLT-600 (task-07) — leyenda + montaje final. `EVOLUTION_A11Y_CHART` es
    // el `aria-label` del contenedor externo del gráfico (fuera de
    // `CrehanaResponsiveContainer`), no un título visual.
    EVOLUTION_A11Y_CHART: 'Gráfico de evolución del avance del ciclo',

    // ANLT-599 (task-06) — fila de bloqueos, cierra el bloque de la historia
    BLOCKS_HEADING: 'Bloqueos que frenan el ciclo',
    BLOCKS_ALERT: 'Hay bloqueos activos en este proceso.',
    BLOCKS_UNAVAILABLE: 'No pudimos calcular los bloqueos de este proceso.',
    BLOCK_WITHOUT_GOALS: 'Sin metas cargadas',
    BLOCK_WITHOUT_GOALS_HINT:
      'Colaboradores que todavía no cargaron ninguna meta.',
    BLOCK_NOT_APPROVED: 'Metas sin aprobar',
    BLOCK_NOT_APPROVED_HINT: 'Metas cargadas que su líder todavía no aprobó.',
    BLOCK_WITHOUT_SELF_REPORT: 'Sin autoevaluación',
    BLOCK_WITHOUT_SELF_REPORT_HINT:
      'Colaboradores que todavía no se autoevaluaron.',

    // ANLT-606 (task-07) — aria-label del botón overlay sobre cada tarjeta de
    // bloqueo. {{label}} es una de las tres BLOCK_* de arriba.
    BLOCKS_CARD_ACTION: 'Ver los colaboradores en: {{label}}',

    // ANLT-606 — M-S5: detalle por colaborador
    DETAIL_HEADING: 'Detalle por colaborador',
    DETAIL_DESCRIPTION:
      'Cada persona inscrita, su etapa y su avance del ciclo de metas.',
    DETAIL_TABLE_TITLE: 'Detalle por colaborador',
    DETAIL_SEARCH_PLACEHOLDER: 'Buscar por nombre',
    DETAIL_COL_COLLABORATOR: 'Colaborador',
    DETAIL_COL_HEADQUARTER: 'Sede',
    DETAIL_COL_AREA: 'Área',
    DETAIL_COL_POSITION: 'Cargo',
    DETAIL_COL_BOSS: 'Líder',
    DETAIL_COL_GOALS: 'Metas',
    DETAIL_COL_GOALS_TOOLTIP: 'Metas cargadas por la persona.',
    DETAIL_COL_PROGRESS: 'Avance del ciclo',
    DETAIL_COL_PROGRESS_TOOLTIP:
      'Porcentaje de metas que llegaron a la etapa final.',
    DETAIL_COL_STAGE: 'Etapa alcanzada',
    DETAIL_COL_STAGE_TOOLTIP:
      'Una etapa cuenta como completada solo cuando TODAS las metas de la persona llegaron a ella.',
    DETAIL_NO_BOSS: 'Sin líder asignado',
    DETAIL_NO_DATA_SR: 'Sin dato',
    DETAIL_STAGE_NONE: 'Sin metas cargadas',
    DETAIL_STAGE_LOADED: 'Metas creadas',
    DETAIL_STAGE_APPROVED: 'Metas aprobadas',
    DETAIL_STAGE_SELF_REPORTED: 'Autoevaluación',
    DETAIL_STAGE_BOSS_REPORTED: 'Evaluación del líder',
    DETAIL_EMPTY_TITLE: 'Este proceso no tiene inscritos',
    DETAIL_EMPTY_DESCRIPTION: 'Todavía no hay colaboradores en este proceso.',
    DETAIL_SEARCH_EMPTY_TITLE: 'Ningún colaborador coincide con tu búsqueda',
    DETAIL_SEARCH_EMPTY_DESCRIPTION:
      'Prueba con otro nombre, o borra la búsqueda para ver a todos los inscritos.',
    DETAIL_ERROR_TITLE: 'No pudimos cargar el detalle por colaborador',

    // ANLT-602 — M-S3: avance por segmento (task-02: columnas de la tabla)
    SEGMENT_TABLE_TITLE: 'Avance por grupo',
    SEGMENT_COL_GROUP: 'Grupo',
    SEGMENT_COL_ENROLLED: 'Colaboradores',
    SEGMENT_COL_ENROLLED_TOOLTIP: 'Colaboradores inscritos en este grupo.',
    SEGMENT_COL_PROGRESS: 'Avance del ciclo',
    SEGMENT_COL_PROGRESS_TOOLTIP:
      'Etapas completadas ÷ (inscritos del grupo × etapas del ciclo).',
    SEGMENT_STAGE_TOOLTIP: '% sobre los colaboradores de ese grupo.',

    // ANLT-602 — M-S3 (task-03: cabecera/descripción/truncación/nota/vacío sin filtrar/error)
    SEGMENT_HEADING: 'Avance del ciclo por segmento',
    SEGMENT_DESCRIPTION:
      'Qué tan avanzado está cada grupo en las etapas del ciclo.',
    SEGMENT_TRUNCATED:
      'Mostrando los {{shown}} grupos con menor avance, de {{total}} en total.',
    SEGMENT_NOTE:
      'Cada porcentaje es sobre los colaboradores de ese grupo, no sobre el total de la organización. Los grupos sin nadie inscrito se muestran con guion.',
    SEGMENT_EMPTY_TITLE: 'Este proceso todavía no tiene inscritos',
    SEGMENT_EMPTY_DESCRIPTION:
      'Todavía no hay grupos que mostrar para este proceso.',
    SEGMENT_ERROR_TITLE: 'No pudimos cargar el avance por segmento',

    // ANLT-602 — M-S3 (task-04: selector + vacío CON atributo elegido — cierra la historia)
    SEGMENT_SELECT_LABEL: 'Visualizar por',
    SEGMENT_SELECT_PLACEHOLDER: 'Elige un atributo',
    SEGMENT_GROUP_STANDARD: 'Atributos estándar',
    SEGMENT_GROUP_CUSTOM: 'Campos personalizados',
    SEGMENT_GROUP_CUSTOM_TAG: 'De tu empresa',
    SEGMENT_ATTRIBUTE_EMPTY_TITLE: 'Este atributo no arroja ningún grupo',
    SEGMENT_ATTRIBUTE_EMPTY_DESCRIPTION:
      'Prueba con otro atributo, o vuelve al corte por omisión.',

    // ANLT-606 (task-06) — filtro de situación (D-15). "BLOCK_*" se reutiliza
    // de ANLT-599 sin editar. DETAIL_FILTERED_EMPTY_* es la SEGUNDA causa de
    // vacío filtrado (la primera es la búsqueda, DETAIL_SEARCH_EMPTY_*):
    // filtrar por «sin metas cargadas» en un proceso con inscritos tampoco es
    // "este proceso no tiene inscritos".
    DETAIL_FILTER_LABEL: 'Situación',
    DETAIL_FILTER_ALL: 'Todos los inscritos',
    DETAIL_FILTERED_EMPTY_TITLE: 'No hay colaboradores en esta situación',
    DETAIL_FILTERED_EMPTY_DESCRIPTION:
      'Prueba con otra situación, o quita el filtro para ver a todos los inscritos.',

    // ANLT-604 — M-S4: avance por líder (task-02: columnas de la tabla)
    LEADERS_TABLE_TITLE: 'Avance por líder',
    LEADERS_SEARCH_PLACEHOLDER: 'Buscar por nombre',
    LEADERS_COL_LEADER: 'Líder',
    LEADERS_COL_TEAM: 'Equipo',
    LEADERS_COL_PROGRESS: 'Avance del ciclo',

    // ANLT-604 — M-S4: avance por líder (task-03: sección, estados)
    LEADERS_HEADING: 'Avance por líder',
    LEADERS_DESCRIPTION:
      'Qué tan avanzado está el equipo de cada líder en las etapas del ciclo.',
    LEADERS_EMPTY_TITLE:
      'Este proceso todavía no tiene líderes con equipo inscrito',
    LEADERS_EMPTY_DESCRIPTION:
      'Cuando haya colaboradores inscritos, sus líderes aparecerán aquí.',
    LEADERS_SEARCH_EMPTY_TITLE: 'Ningún líder coincide con tu búsqueda',
    LEADERS_SEARCH_EMPTY_DESCRIPTION:
      'Prueba con otro nombre, o borra la búsqueda para ver a todos los líderes.',
    LEADERS_ERROR_TITLE: 'No pudimos cargar el avance por líder',

    // ANLT-604 — M-S4: avance por líder (task-04: columnas de etapa). Las etiquetas de cada
    // etapa (STAGE_LOADED, etc.) se REUSAN de ANLT-599 vía STAGE_LABEL_KEY — no se redefinen.
    LEADERS_NOTE:
      'Cada porcentaje es sobre el equipo inscrito de ese líder. Las etapas alcanzadas y las pendientes son dos lecturas distintas: no se suman.',
    LEADERS_STAGE_TOOLTIP:
      'Arriba, cuántos del equipo alcanzaron esta etapa. Abajo, cuántos siguen pendientes. Son dos lecturas distintas: no se suman.',
    LEADERS_PENDING: '{{count}} pendiente',
    LEADERS_PENDING_plural: '{{count}} pendientes',

    // ANLT-610 — M-R2: distribución del cumplimiento (semáforo, task-02:
    // cabecera + cinco estados + AC-3 "sin escala"). El bloque cuenta
    // COLABORADORES, no metas (P-7, contrato v5) — la copy nunca dice "metas".
    DISTRIBUTION_HEADING: 'Distribución del cumplimiento',
    DISTRIBUTION_DESCRIPTION:
      'Cuántos colaboradores caen en cada rango de cumplimiento de la escala.',
    DISTRIBUTION_LOADING_LABEL: 'Cargando distribución del cumplimiento',
    DISTRIBUTION_ERROR_TITLE:
      'No pudimos cargar la distribución del cumplimiento',
    DISTRIBUTION_EMPTY_TITLE:
      'Este proceso todavía no tiene colaboradores evaluados',
    DISTRIBUTION_EMPTY_DESCRIPTION:
      'Cuando haya colaboradores con resultado, aparecerán aquí distribuidos por rango.',
    DISTRIBUTION_NO_SCALE_TITLE:
      'Este ciclo no tiene una escala de cumplimiento configurada',
    DISTRIBUTION_NO_SCALE_DESCRIPTION:
      'Configura una escala de cumplimiento para ver a los colaboradores distribuidos por rango.',

    // ANLT-610 — M-R2 (task-03: resolutores de banda + leyenda de la
    // escala, AC-1 mitad leyenda). El texto de cada Tag es `band.label`
    // verbatim — estas claves solo cubren la etiqueta de la fila y el rango.
    // Sin «%»: el SDL dice que `upper_bound` de la banda más alta es el
    // `max_scale` del proceso, que puede superar 100 — no es garantizado un
    // porcentaje.
    DISTRIBUTION_SCALE_LABEL: 'Escala de cumplimiento',
    DISTRIBUTION_RANGE_BOTH: '{{lower}} – {{upper}}',
    DISTRIBUTION_RANGE_UP_TO: 'Hasta {{upper}}',
    DISTRIBUTION_RANGE_FROM: 'Desde {{lower}}',

    // ANLT-610 — M-R2 (task-04: gráfico de barras apiladas, AC-1 mitad
    // gráfico). Las tres series son semánticas (nunca por posición), y el
    // eje Y cuenta COLABORADORES, nunca "metas" (P-7).
    DISTRIBUTION_SERIES_CRITICAL: 'Crítico',
    DISTRIBUTION_SERIES_COMPLIANT: 'Cumple',
    DISTRIBUTION_SERIES_BELOW: 'Por debajo',
    DISTRIBUTION_AXIS_X: 'Banda de cumplimiento',
    DISTRIBUTION_AXIS_Y: 'Colaboradores',
    DISTRIBUTION_A11Y_CHART: 'Gráfico de distribución del cumplimiento',

    // ANLT-610 — M-R2 (task-05: nota de evaluados + nota "sin resultado" +
    // sub-estado "nadie evaluado", AC-2, cierra MR-B). "Sin resultado"
    // llega aparte del backend y NUNCA se pinta como una banda más — meterla
    // dentro convertiría "no lo evaluaron" en "le fue pésimo". La nota no
    // afirma que bandas + sin_resultado = inscritos (`enrolled_count` no
    // viaja en este payload, vive en M-R1).
    DISTRIBUTION_EVALUATED_NOTE:
      'Los porcentajes se calculan sobre {{count}} colaboradores evaluados.',
    DISTRIBUTION_WITHOUT_RESULT_TITLE: 'Sin resultado',
    DISTRIBUTION_WITHOUT_RESULT_BODY:
      '{{count}} colaboradores todavía no tienen resultado: no forman parte de la escala. No haber sido evaluado no es haber obtenido cero.',
    DISTRIBUTION_NO_EVALUATED_TITLE: 'Todavía nadie tiene resultado',
    DISTRIBUTION_NO_EVALUATED_DESCRIPTION:
      'Cuando haya colaboradores evaluados en este proceso, verás cómo se distribuyen en la escala.',

    // ANLT-609 (task-04) — banda de 5 tarjetas (M-R1)
    RESULTS_A11Y_BAND: 'Indicadores de resultados del proceso',

    RESULTS_OFFICIAL_LABEL: 'Cumplimiento oficial',
    RESULTS_OFFICIAL_TOOLTIP:
      'Porcentaje de los colaboradores evaluados que alcanzó el rango umbral configurado por tu organización. Cuenta personas: no es el promedio de sus notas, y por eso no se mueve con un caso extremo.',
    RESULTS_OFFICIAL_DESCRIPTION:
      'Cuenta personas: {{compliant}} de {{evaluated}} evaluados alcanzaron el umbral.',

    RESULTS_COMPLIANT_LABEL: 'Colaboradores que cumplieron',
    RESULTS_COMPLIANT_TOOLTIP:
      'Personas cuyo resultado individual alcanzó el rango «{{band}}» o superior, según los rangos que configuró tu organización.',
    RESULTS_COMPLIANT_DESCRIPTION:
      'De {{evaluated}} evaluados · rango «{{band}}» o superior.',
    RESULTS_COMPLIANT_DESCRIPTION_NO_BAND:
      'De {{evaluated}} evaluados · alcanzaron el umbral.',

    RESULTS_CRITICAL_LABEL: 'En estado crítico',
    RESULTS_CRITICAL_TOOLTIP:
      'Personas cuyo resultado individual cayó en el rango más bajo configurado por tu organización. Es el grupo que requiere plan de acción.',
    RESULTS_CRITICAL_DESCRIPTION:
      'De {{evaluated}} evaluados · rango «{{band}}».',
    RESULTS_CRITICAL_DESCRIPTION_NO_BAND:
      'De {{evaluated}} evaluados · en el rango más bajo.',

    RESULTS_AVERAGE_LABEL: 'Cumplimiento promedio',
    RESULTS_AVERAGE_TOOLTIP:
      'Media de los resultados individuales de los evaluados. Promedia notas: se mueve entera con un caso extremo, y por eso puede diferir del oficial sin que ninguno de los dos esté mal.',
    RESULTS_AVERAGE_DESCRIPTION: 'Promedia notas de {{evaluated}} evaluados.',
    RESULTS_AVERAGE_DESCRIPTION_SCALE:
      'Promedia notas de {{evaluated}} evaluados · escala 0–{{max}}.',

    RESULTS_COVERAGE_LABEL: 'Cobertura del proceso',
    RESULTS_COVERAGE_TOOLTIP:
      'Porcentaje de los inscritos que terminó con un resultado registrado. Quien no fue evaluado no promedia como cero: se cuenta aparte.',
    RESULTS_COVERAGE_DESCRIPTION:
      '{{evaluated}} evaluados de {{enrolled}} inscritos.',

    // ANLT-609 (task-05) — sección + estados (M-R1)
    RESULTS_HEADING: 'Resultados del proceso',
    RESULTS_DESCRIPTION:
      'Cómo le fue a la organización en el ciclo cerrado. Aquí la palabra es cumplimiento: cuánto se logró de cada meta.',
    RESULTS_TRACK_RANGE: '{{start}} – {{end}}',
    RESULTS_EMPTY_TITLE: 'Este proceso no tiene inscritos',
    RESULTS_EMPTY_DESCRIPTION:
      'Cuando haya colaboradores inscritos y evaluados, aquí verás el resultado del proceso.',
    RESULTS_ERROR_TITLE: 'No pudimos cargar los resultados del proceso',

    // ANLT-609 (task-07, MR-B) — micro-estado sin escala (AC-3)
    RESULTS_NO_SCALE_CARD: 'Sin escala configurada.',
    RESULTS_NO_SCALE_MESSAGE:
      'Este proceso no tiene rangos configurados, así que no hay umbral que alcanzar: el cumplimiento oficial, los colaboradores que cumplieron y los críticos no se pueden calcular. No son cero.',

    // ANLT-609 (task-08, MR-B) — declaración de no cubiertos (AC-4)
    RESULTS_UNCOVERED_MESSAGE:
      '{{count}} colaboradores con metas no aparecen en este tablero porque no tienen un registro activo. El reporte descargable sí los incluye, agrupados en una sola fila sin nombre.',

    // ANLT-617 (task-02) — `GoalsSegmentResultsBars`, fila de grupo (M-R3)
    RESULTS_GROUP_COVERAGE: '{{evaluated}} de {{total}} evaluados',
    RESULTS_GROUP_WITHOUT_RESULT: '{{count}} sin resultado',
    RESULTS_GROUP_AVERAGE_CAPTION: 'promedio',

    // ANLT-617 (task-03) — `GoalsSegmentResultsSection`, cabecera + 5 estados + totales (M-R3)
    RESULTS_GROUP_HEADING: 'Cumplimiento por grupo',
    RESULTS_GROUP_DESCRIPTION:
      'Cómo se reparte el cumplimiento entre las áreas, sedes o cargos de tu organización.',
    RESULTS_GROUP_TOTALS:
      '{{groups}} grupos · {{enrolled}} inscritos · {{evaluated}} evaluados',
    RESULTS_GROUP_NOTE:
      'Promedia PERSONAS: el resultado individual de cada colaborador evaluado del grupo, en la escala que configuró tu organización. No es comparable con un promedio que cuente metas.',
    RESULTS_GROUP_TRUNCATED:
      'Mostramos {{shown}} de {{total}} grupos. Los que faltan no están sumados en esta lista.',
    RESULTS_GROUP_ATTRIBUTE_EMPTY_TITLE: 'Este atributo no arroja ningún grupo',
    RESULTS_GROUP_ATTRIBUTE_EMPTY_DESCRIPTION:
      'El proceso puede tener inscritos: lo que no arroja grupos es este corte. Prueba con otro atributo, o vuelve al corte por omisión.',
    RESULTS_GROUP_EMPTY_TITLE: 'Este proceso no tiene inscritos',
    RESULTS_GROUP_EMPTY_DESCRIPTION:
      'Cuando haya colaboradores inscritos y evaluados, aquí verás el corte por grupo.',
    RESULTS_GROUP_ERROR_TITLE: 'No pudimos cargar el cumplimiento por grupo',

    // ANLT-617 (task-04) — `row-track`, la pista apilada (M-R3)
    RESULTS_GROUP_A11Y_BAR: 'Reparto de {{group}} entre los rangos',

    // ANLT-617 (task-05) — micro-estado «sin escala» + nota de reparto (M-R3)
    RESULTS_GROUP_NO_SCALE:
      'Tu organización no configuró rangos para este proceso, así que no hay reparto que mostrar. El promedio de cada grupo sí.',
    RESULTS_GROUP_DISTRIBUTION_NOTE:
      'La barra muestra cómo se reparte cada grupo entre los rangos; la cifra es su promedio. Dos grupos con el mismo promedio pueden tener repartos opuestos.',

    // ANLT-614 (task-01) — `GoalsDisciplineSection`, cabecera + 5 estados + sub-estado
    // «sin ciclos transcurridos» (M-R5). Prefijo DISCIPLINE_, NUNCA RESULTS_ (ese prefijo
    // ya tiene su propia prueba de paridad y no debe contaminarse mientras las ramas
    // paralelas de Resultados están a medias).
    DISCIPLINE_HEADING: 'Disciplina de seguimiento durante el ciclo',
    DISCIPLINE_DESCRIPTION:
      'Qué tan disciplinado fue el proceso de seguimiento, no el resultado que se obtuvo.',
    DISCIPLINE_WHAT_IT_MEASURES:
      'Mide si la persona reportó todas sus metas aprobadas en todos los ciclos transcurridos. No es la columna «Cumple» del reporte descargable, que mide si el jefe evaluó.',
    DISCIPLINE_NO_CYCLES_TITLE: 'Todavía no hay nada que medir',
    DISCIPLINE_NO_CYCLES_DESCRIPTION:
      'Ningún ciclo de este proceso ha vencido todavía. Cuando el primero venza, aquí verás quién sostuvo el ritual de reportar.',
    DISCIPLINE_LOADING_LABEL: 'Cargando disciplina de seguimiento',
    DISCIPLINE_ERROR_TITLE: 'No pudimos cargar la disciplina de seguimiento',
    DISCIPLINE_EMPTY_TITLE: 'Este proceso no tiene inscritos',
    DISCIPLINE_EMPTY_DESCRIPTION:
      'Cuando haya colaboradores inscritos, aquí verás quién sostuvo el ritual de reportar sus metas.',

    // ANLT-614 (task-02) — `GoalsDisciplineKpiBand` + línea de ciclos medidos (AC-3, G-2/G-4).
    // El porcentaje puede pintarse '—' mientras los tres conteos llevan su cifra real, incluido
    // un 0 medido — son causas distintas y se declaran por separado (ver los tooltips).
    DISCIPLINE_OBSERVED_TRACKS: 'Medido sobre {{tracks}} ciclos transcurridos',
    DISCIPLINE_UP_TO_DATE_LABEL: 'Al día',
    DISCIPLINE_UP_TO_DATE_TOOLTIP:
      'Reportó todas sus metas aprobadas en todos los ciclos ya en curso.',
    DISCIPLINE_PENDING_LABEL: 'Pendientes',
    DISCIPLINE_PENDING_TOOLTIP:
      'Tenían metas aprobadas que reportar y todavía deben algún seguimiento.',
    DISCIPLINE_WITHOUT_GOALS_LABEL: 'Sin metas',
    DISCIPLINE_WITHOUT_GOALS_TOOLTIP:
      'No tenían ninguna meta aprobada: no cuentan ni como al día ni como pendientes.',
    DISCIPLINE_WITHOUT_GOALS_DESCRIPTION: 'No tenían nada que reportar',
    DISCIPLINE_PERCENTAGE_LABEL: '% de disciplina',
    DISCIPLINE_PERCENTAGE_TOOLTIP:
      'Sobre quienes tenían metas aprobadas que reportar. No incluye a quienes no tenían nada que reportar.',
    DISCIPLINE_PERCENTAGE_UNMEASURABLE:
      'Nadie tenía metas aprobadas que reportar',
    DISCIPLINE_A11Y_BAND: 'Indicadores de disciplina de seguimiento',

    // ANLT-614 (task-03) — `GoalsDisciplineGroupsChart` + `SegmentAttributeSelect` (AC-4,
    // G-5/G-7..G-10). El vacío del corte tiene DOS causas: si hay un atributo elegido, el
    // mensaje NOMBRA a los inscritos — JAMÁS «este proceso no tiene inscritos», que ya se le
    // dijo a un proceso con 412 personas dos veces en esta épica.
    DISCIPLINE_CUT_EMPTY_TITLE: 'Este corte no devolvió grupos',
    DISCIPLINE_CUT_EMPTY_DESCRIPTION:
      'Hay {{enrolled}} personas inscritas; prueba con otro atributo',
    DISCIPLINE_GROUPS_EMPTY_TITLE: 'Todavía no hay grupos que mostrar',
    DISCIPLINE_NO_MEASURABLE_GROUPS:
      'Ningún grupo tenía metas aprobadas que reportar',
    DISCIPLINE_GROUPS_EXCLUDED:
      'Sin barra en el gráfico (nadie tenía metas aprobadas que reportar): {{groups}}',
    DISCIPLINE_TRUNCATED: '{{shown}} de {{total}}',
    DISCIPLINE_CHART_SERIES_LABEL: '% de disciplina',
    // ANLT-615 (task-03) — `GoalsByCategorySection`, cabecera + 5 estados
    // (M-R4). Prefijo propio `CATEGORY_`: cero claves compartidas con
    // `RESULTS_*`/`RESULTS_GROUP_*` — este bloque promedia METAS
    // (`average_goal_result`), no personas.
    CATEGORY_HEADING: 'Cumplimiento por categoría de meta',
    CATEGORY_UNIT_NOTE:
      'Este bloque promedia metas, no personas: el resultado no es comparable con los indicadores individuales de otros bloques de este ciclo.',
    CATEGORY_UNIT_TOOLTIP:
      'Cada categoría promedia el resultado de las metas que la componen, sin importar quién las creó. No es el mismo número que el resultado individual de cada persona en otros bloques.',
    CATEGORY_LOADING_LABEL: 'Cargando cumplimiento por categoría',
    CATEGORY_ERROR_TITLE: 'No pudimos cargar el cumplimiento por categoría',
    CATEGORY_EMPTY_TITLE: 'Este proceso no tiene metas registradas',
    CATEGORY_EMPTY_DESCRIPTION:
      'Cuando haya metas creadas para este proceso, aquí verás el cumplimiento por categoría.',
    // ANLT-615 (task-04) — `GoalsByCategoryCut`, claves internas del corte
    // (no dependen de si la instancia es «estrategias» o «enfoques»; esas
    // dos etiquetas las añade task-05).
    CATEGORY_UNEVALUATED_TITLE: 'Todavía sin metas evaluadas',
    CATEGORY_CAP_NOTE: 'Mostrando {{shown}} de {{total}} categorías',
    CATEGORY_CUT_EMPTY: 'Este corte no tiene categorías',
    CATEGORY_SERIES_AVERAGE: 'Promedio de cumplimiento',
    // ANLT-615 (task-05) — cablea los dos cortes dentro de la sección
    // (etiqueta de cada instancia) + la nota de cobertura. Cierra la
    // historia.
    CATEGORY_CUT_STRATEGIES: 'Meta estratégica',
    CATEGORY_CUT_APPROACHES: 'Enfoque',
    CATEGORY_UNCOVERED_NOTE:
      'Este cálculo no incluye {{count}} metas cuyo responsable no tiene un registro activo. El reporte descargable sí las incluye.',

    // ANLT-618 (task-01) — `GoalsRankingSection`, ranking de colaboradores
    // (M-R6). Prefijo propio `RANKING_`, guarda de vocabulario propia
    // (i18n/ranking-vocabulary.test.ts). `RANKING_COL_RESULT_TOOLTIP` dice
    // "nota", nunca "%" — `result` puede exceder 100 (max_scale, §3.1 del
    // plan). Las claves de desglose (`RANKING_GOALS_*`) las añade task-03.
    RANKING_HEADING: 'Ranking de colaboradores',
    RANKING_DESCRIPTION:
      'El resultado y la banda de cada persona inscrita en este proceso, del mejor al peor cumplimiento.',
    RANKING_TABLE_TITLE: 'Ranking de colaboradores',
    RANKING_COL_COLLABORATOR: 'Colaborador',
    RANKING_COL_RESULT: 'Resultado',
    RANKING_COL_RESULT_TOOLTIP:
      'La nota de la persona sobre la escala configurada para este proceso.',
    RANKING_COL_BAND: 'Banda',
    RANKING_NO_DATA_SR: 'Sin dato',
    RANKING_UNEVALUATED: 'Sin evaluar',
    RANKING_NO_SCALE: 'Sin escala configurada',
    RANKING_EMPTY_TITLE: 'Este proceso no tiene inscritos',
    RANKING_EMPTY_DESCRIPTION:
      'Cuando haya colaboradores inscritos en este proceso, aquí verás su ranking.',
    RANKING_ERROR_TITLE: 'No pudimos cargar el ranking de colaboradores',

    // ANLT-618 (task-02) — búsqueda, orden y columnas de atributo del
    // ranking. `RANKING_NO_BOSS` es el fallback VISIBLE de la columna Jefe,
    // distinto del sr-only `RANKING_NO_DATA_SR` de las otras cuatro.
    RANKING_COL_HEADQUARTER: 'Sede',
    RANKING_COL_AREA: 'Área',
    RANKING_COL_POSITION: 'Cargo',
    RANKING_COL_BOSS: 'Jefe',
    RANKING_NO_BOSS: 'Sin jefe asignado',
    RANKING_SEARCH_PLACEHOLDER: 'Buscar colaborador',
    RANKING_SEARCH_EMPTY_TITLE: 'No encontramos a nadie con ese nombre',
    RANKING_SEARCH_EMPTY_DESCRIPTION:
      'Prueba con otro nombre o revisa la ortografía.',

    // ANLT-618 (task-03) — `GoalsRankingBreakdown`, desglose por meta de la
    // fila expandida. `RANKING_GOAL_WEIGHT` ES un porcentaje (la
    // ponderación); `RANKING_GOAL_RESULT` es una nota 0-100, nunca un "%"
    // (§8.5 del plan) — dos números, dos unidades, en la misma fila.
    RANKING_GOALS_TITLE: 'Desglose por meta',
    RANKING_GOAL_NAME: 'Meta',
    RANKING_GOAL_WEIGHT: 'Peso',
    RANKING_GOAL_RESULT: 'Resultado',
    RANKING_GOALS_EMPTY: 'Esta persona no tiene metas registradas',
    RANKING_GOALS_ERROR: 'No pudimos cargar el desglose de metas',
  },
} as const;
